import { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import axios from "../../../lib/http/axiosConfig";
import CalmSelect from "../Datatab/CropMonitoring/CalmSelect";
import { useT } from "../../../i18n";
import { useDemoScenario } from "../../../demo/DemoScenarioContext";
import { listDemoSections } from "../../../demo/demoSections";
import {
  listDemoSchedules,
  removeDemoSchedule,
  saveDemoSchedule,
  setDemoScheduleEnabled,
} from "../../../demo/demoSchedules";

/**
 * 예약(주간 반복 비행). 실계정은 BE 스케줄러가 발화하고,
 * 데모는 DemoScenarioContext 의 틱이 같은 목록을 읽어 발화한다(브라우저가 켜져 있는 동안만).
 * 농장당 여러 개를 두고, 예약마다 요일·시각·구역(섹션, 기본 전체)을 갖는다.
 * 편집 폼은 하나만 두고 목록에서 고른 예약을 그 폼에서 고친다. 새 예약도 같은 폼으로 만든다.
 */
const DAYS = ["SUN", "MON", "TUE", "WED", "THU", "FRI", "SAT"];
const pad = (n) => String(n).padStart(2, "0");
const emptyForm = () => ({ id: null, days: [], hour: "07", minute: "00", sectionId: "" });

const formatNextRun = (t, value) => {
  if (!value) return "-";
  // BE 는 Asia/Seoul 벽시계 시각을 시간대 없이 준다. 그대로 파싱하면 로컬(KST)로 읽힌다.
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return t("scheduler.schedule.nextRun.format", {
    month: d.getMonth() + 1,
    day: d.getDate(),
    weekday: t(`scheduler.schedule.day.${DAYS[d.getDay()]}`),
    time: `${pad(d.getHours())}:${pad(d.getMinutes())}`,
  });
};

export default function SchedulerPanel({ refreshKey = 0 }) {
  const t = useT();
  const { isDemo } = useDemoScenario();
  const farmId = useSelector((state) => state.farm.farmId);
  const droneId = useSelector((state) => state.drone.selectedDrone);
  const [schedules, setSchedules] = useState([]);
  const [sections, setSections] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    if (isDemo) {
      // 데모는 BE 를 타지 않는다. 예약도 섹션도 같은 브라우저에 있다.
      setSchedules(listDemoSchedules());
      setSections(listDemoSections(farmId));
      return;
    }
    if (!farmId) return;
    try {
      const [s, sec] = await Promise.all([
        axios.get(`/api/schedules/${farmId}`),
        axios.get(`/api/farms/${farmId}/sections`),
      ]);
      setSchedules(Array.isArray(s.data?.data) ? s.data.data : []);
      setSections(Array.isArray(sec.data?.data) ? sec.data.data : []);
    } catch (error) {
      console.warn("[SCHEDULE] 조회 실패", error);
    }
  }, [farmId, isDemo]);

  useEffect(() => {
    load();
  }, [load, refreshKey]);

  const sectionName = (id) => sections.find((s) => s.id === id)?.name ?? null;
  const errorCode = (error) => error?.response?.data?.error?.code;
  const explain = (error, fallback) => {
    const code = errorCode(error);
    if (code === "SCHEDULE-004") return t("scheduler.schedule.error.duplicate");
    if (code === "SECTION-001") return t("scheduler.schedule.error.sectionMissing");
    return fallback;
  };
  const dayLabel = (d) => t(`scheduler.schedule.day.${d}`);

  const startEdit = (s) => {
    setForm({
      id: s.id,
      days: Array.isArray(s.daysOfWeek) ? [...s.daysOfWeek] : [],
      hour: pad(s.hour ?? 0),
      minute: pad(s.minute ?? 0),
      sectionId: s.sectionId ? String(s.sectionId) : "",
    });
  };

  const toggleDay = (day) =>
    setForm((f) => ({ ...f, days: f.days.includes(day) ? f.days.filter((d) => d !== day) : [...f.days, day] }));

  const clampTime = (value, max) => {
    const n = parseInt(String(value).replace(/[^0-9]/g, ""), 10);
    if (Number.isNaN(n)) return "00";
    return pad(Math.min(max, Math.max(0, n)));
  };

  const save = async () => {
    if (!isDemo && (!farmId || !droneId)) {
      toast.warn(t("scheduler.schedule.toast.needTarget"), { toastId: "schedule-target" });
      return;
    }
    if (form.days.length === 0) {
      toast.warn(t("scheduler.schedule.toast.needDays"), { toastId: "schedule-days" });
      return;
    }
    // 실계정 섹션 id 는 숫자지만 데모는 "demo-section-…" 문자열이다.
    // 여기서 Number() 를 태우면 NaN 이 되고, localStorage 에 JSON 으로 저장되는 순간
    // null 로 뭉개져서 예약이 섹션을 잃고 전체 칸으로 돈다.
    const sectionId = form.sectionId
      ? (isDemo ? form.sectionId : Number(form.sectionId))
      : null;
    const payload = {
      title: sectionId ? `${sectionName(sectionId) ?? "섹션"} schedule` : "FULL_SCAN schedule",
      timeZone: "Asia/Seoul",
      timeOfDay: { hour: Number(form.hour), minute: Number(form.minute) },
      daysOfWeek: DAYS.filter((d) => form.days.includes(d)),
      target: { droneId },
      sectionId,
    };
    setSaving(true);
    try {
      if (isDemo) {
        saveDemoSchedule(form.id, { ...payload, sectionName: sectionName(sectionId) });
      } else if (form.id) {
        await axios.put(`/api/schedules/${farmId}/${form.id}`, payload);
      } else {
        await axios.post(`/api/schedules/${farmId}`, payload);
      }
      toast.success(form.id ? t("scheduler.schedule.toast.updated") : t("scheduler.schedule.toast.created"));
      setForm(emptyForm());
      load();
    } catch (error) {
      toast.error(explain(error, t("scheduler.schedule.toast.saveFailed")), { toastId: "schedule-save" });
    } finally {
      setSaving(false);
    }
  };

  const toggleEnabled = async (s) => {
    const next = !s.enabled;
    setSchedules((list) => list.map((x) => (x.id === s.id ? { ...x, enabled: next } : x)));
    try {
      if (isDemo) setDemoScheduleEnabled(s.id, next);
      else await axios.patch(`/api/schedules/${farmId}/${s.id}/enabled`, null, { params: { enabled: next } });
      load();
    } catch (error) {
      setSchedules((list) => list.map((x) => (x.id === s.id ? { ...x, enabled: !next } : x)));
      toast.error(explain(error, t("scheduler.schedule.toast.toggleFailed")), { toastId: "schedule-toggle" });
    }
  };

  const remove = async (s) => {
    if (!window.confirm(t("scheduler.schedule.confirm.delete"))) return;
    try {
      if (isDemo) removeDemoSchedule(s.id);
      else await axios.delete(`/api/schedules/${farmId}/${s.id}`);
      if (form.id === s.id) setForm(emptyForm());
      load();
    } catch (error) {
      toast.error(t("scheduler.schedule.toast.deleteFailed"), { toastId: "schedule-delete" });
    }
  };

  return (
    <section className="sb-card scheduler-card">
      <div className="sb-title-with-help">
        <h3 className="sb-card-title">{t("scheduler.schedule.title")}</h3>
        <button
          type="button"
          className="sb-help"
          aria-label={t("scheduler.schedule.helpAria")}
          data-tip={t("scheduler.schedule.helpTip")}
        >
          ?
        </button>
      </div>

      {/* 데모는 BE 스케줄러가 없어 브라우저가 대신 시각을 본다. 화면을 닫으면 안 돈다는 걸
          눌러 보기 전에 알려 준다 — 실계정 예약과 성질이 다른 부분이다. */}
      {isDemo && <div className="sec-empty">{t("scheduler.schedule.demo.notice")}</div>}

      <div className="sch-list">
        {schedules.length === 0 && <div className="sec-empty">{t("scheduler.schedule.empty")}</div>}
        {schedules.map((s) => {
          // 다음 실행·최근 실패 문구. 데스크톱은 폭이 모자라면 말줄임 + title 로 전문을 보여주고,
          // 모바일은 hover 가 없어 CSS 에서 줄바꿈으로 다 보이게 한다.
          const nextText =
            (s.enabled
              ? t("scheduler.schedule.row.next", { next: formatNextRun(t, s.nextRunAt) })
              : t("scheduler.schedule.row.off")) +
            (s.lastStatus === "FAILED" && s.lastError
              ? t("scheduler.schedule.row.lastFailed", { error: s.lastError })
              : "");
          return (
          <div className={`sch-row${s.enabled ? "" : " off"}${form.id === s.id ? " editing" : ""}`} key={s.id}>
            <label
              className="scheduler-switch"
              title={s.enabled ? t("scheduler.schedule.row.switchOff") : t("scheduler.schedule.row.switchOn")}
            >
              <input type="checkbox" checked={Boolean(s.enabled)} onChange={() => toggleEnabled(s)} />
              <span className="scheduler-slider" />
            </label>
            <button type="button" className="sch-body" onClick={() => startEdit(s)} title={t("scheduler.schedule.row.edit")}>
              <div className="sch-line">
                <span className="sch-time">{pad(s.hour ?? 0)}:{pad(s.minute ?? 0)}</span>
                <span className="sch-days">
                  {DAYS.map((d) => (
                    <i key={d} className={(s.daysOfWeek || []).includes(d) ? "on" : ""}>
                      {dayLabel(d)}
                    </i>
                  ))}
                </span>
              </div>
              <div className="sch-sub">
                <span>{s.sectionName ?? t("scheduler.schedule.row.allCells")}</span>
                <span className="sch-next" title={nextText}>
                  {nextText}
                </span>
              </div>
            </button>
            <button
              type="button"
              className="sec-btn icon danger"
              title={t("scheduler.schedule.row.delete")}
              aria-label={t("scheduler.schedule.row.delete")}
              onClick={() => remove(s)}
            >
              ×
            </button>
          </div>
          );
        })}
      </div>

      <div className="sch-form">
        <div className="sch-form-head">
          <span>
            {form.id
              ? t("scheduler.schedule.form.editTitle", { time: `${pad(form.hour)}:${pad(form.minute)}` })
              : t("scheduler.schedule.form.newTitle")}
          </span>
          {form.id && (
            <button type="button" className="sec-btn" onClick={() => setForm(emptyForm())}>
              {t("scheduler.schedule.form.resetToNew")}
            </button>
          )}
        </div>
        <div className="scheduler-repeat">
          {DAYS.map((d) => (
            <button
              type="button"
              key={d}
              className={`scheduler-day${form.days.includes(d) ? " is-active" : ""}`}
              aria-pressed={form.days.includes(d)}
              onClick={() => toggleDay(d)}
            >
              {dayLabel(d)}
            </button>
          ))}
        </div>
        <div className="sch-form-row">
          <div className="scheduler-time-input">
            <input
              className="time-box"
              value={form.hour}
              inputMode="numeric"
              aria-label={t("scheduler.schedule.form.hour")}
              onChange={(e) => setForm((f) => ({ ...f, hour: e.target.value.replace(/[^0-9]/g, "").slice(0, 2) }))}
              onBlur={() => setForm((f) => ({ ...f, hour: clampTime(f.hour, 23) }))}
            />
            <span className="time-sep">:</span>
            <input
              className="time-box"
              value={form.minute}
              inputMode="numeric"
              aria-label={t("scheduler.schedule.form.minute")}
              onChange={(e) => setForm((f) => ({ ...f, minute: e.target.value.replace(/[^0-9]/g, "").slice(0, 2) }))}
              onBlur={() => setForm((f) => ({ ...f, minute: clampTime(f.minute, 59) }))}
            />
          </div>
          <CalmSelect
            ariaLabel={t("scheduler.schedule.form.section")}
            value={form.sectionId}
            options={[
              { value: "", label: t("scheduler.schedule.form.allCellsDefault") },
              ...sections.map((s) => ({
                value: String(s.id),
                label: t("scheduler.schedule.form.sectionOption", { name: s.name, count: s.cellIds.length }),
              })),
            ]}
            onChange={(v) => setForm((f) => ({ ...f, sectionId: v }))}
          />
        </div>
        <button type="button" className="scheduler-confirm" disabled={saving} onClick={save}>
          {form.id ? t("scheduler.schedule.form.saveEdit") : t("scheduler.schedule.form.add")}
        </button>
      </div>
    </section>
  );
}
